import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  useGetCollaboratori,
  useCreateCollaboratore,
  useUpdateCollaboratore,
  useGetStrutture,
} from "@/lib/supabaseQueries";
import { useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, CheckCircle, User, Briefcase, Building2, ToggleRight, FileText } from "lucide-react";
import { useParentPath } from "@/lib/useParentPath";
import { usePermissions } from "@/lib/usePermissions";

const ACCENT = "var(--primary)";
const inp = "w-full px-3 py-2.5 rounded-xl border border-border bg-background text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-primary/30 transition-shadow placeholder:text-muted-foreground/50";
const lbl = "block text-xs font-medium text-muted-foreground mb-1.5";

function Section({ icon: Icon, title, children }: { icon: any; title: string; children: React.ReactNode }) {
  return (
    <div className="bg-card rounded-2xl border border-border overflow-hidden">
      <div className="flex items-center gap-2.5 px-5 py-3.5 border-b border-border bg-muted/10">
        <Icon className="h-4 w-4 flex-shrink-0" style={{ color: ACCENT }} />
        <span className="text-sm font-semibold text-foreground">{title}</span>
      </div>
      <div className="p-5 space-y-4">{children}</div>
    </div>
  );
}
function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className={lbl}>{label}</label>{children}</div>;
}
const TUTTI_RUOLI = ["Pulizie", "Manutenzione", "Giardinaggio", "Receptionist", "Assistente", "Autista", "Altro"];

function parseJson(s: any, fallback: any = []) {
  try { return JSON.parse(s || JSON.stringify(fallback)); } catch { return fallback; }
}

const emptyForm = {
  nome: "", cognome: "", email: "", telefono: "",
  ruolo: "Pulizie", ruoli: "[]", strutture_ids: "[]", strutture_nomi: "[]",
  attivo: true, note: "",
};

export default function CollaboratorePage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === "nuovo";
  const navigate = useNavigate();
  const goBack = useParentPath();
  const qc = useQueryClient();

  const { data: allCollaboratori = [], isLoading } = useGetCollaboratori({ query: { enabled: !isNew } });
  const existing = isNew ? null : (allCollaboratori as any[]).find((c: any) => c.id === Number(id));
  const { data: strutture = [] } = useGetStrutture();
  const { isAdmin } = usePermissions();
  const create = useCreateCollaboratore();
  const update = useUpdateCollaboratore();

  const [form, setForm] = useState<typeof emptyForm>(emptyForm);
  const [saved, setSaved] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    if (existing && !isNew) {
      const ex = existing as any;
      const ruoli = parseJson(ex.ruoli, []);
      setForm({
        nome: ex.nome ?? "",
        cognome: ex.cognome ?? "",
        email: ex.email ?? "",
        telefono: ex.telefono ?? "",
        ruolo: ex.ruolo ?? "Pulizie",
        ruoli: ruoli.length > 0 ? ex.ruoli : JSON.stringify([ex.ruolo || "Pulizie"]),
        strutture_ids: ex.strutture_ids ?? "[]",
        strutture_nomi: ex.strutture_nomi ?? "[]",
        attivo: ex.attivo ?? true,
        note: ex.note ?? "",
      });
    }
  }, [existing, isNew]);

  const set = (k: string, v: any) => setForm(f => ({ ...f, [k]: v }));
  const ruoli: string[] = parseJson(form.ruoli);
  const strutture_ids: number[] = parseJson(form.strutture_ids, []).map(Number);

  const toggleRuolo = (r: string) => {
    const next = ruoli.includes(r) ? ruoli.filter(x => x !== r) : [...ruoli, r];
    set("ruoli", JSON.stringify(next));
    set("ruolo", next[0] ?? "Altro");
  };
  const toggleStruttura = (s: any) => {
    const currNomi: string[] = parseJson(form.strutture_nomi, []);
    if (strutture_ids.includes(s.id)) {
      const i = strutture_ids.indexOf(s.id);
      set("strutture_ids", JSON.stringify(strutture_ids.filter(x => x !== s.id)));
      set("strutture_nomi", JSON.stringify(currNomi.filter((_, idx) => idx !== i)));
    } else {
      set("strutture_ids", JSON.stringify([...strutture_ids, s.id]));
      set("strutture_nomi", JSON.stringify([...currNomi, s.nome]));
    }
  };

  const handleSave = () => {
    if (!form.nome || !isAdmin) return;
    setSaveError(null);
    const clean = { ...form, ruolo: ruoli[0] || "Altro" };
    const opts = {
      onSuccess: (res: any) => {
        qc.invalidateQueries({ queryKey: ["collaboratori"] });
        setSaved(true);
        setTimeout(() => setSaved(false), 2000);
        if (isNew && res?.id) navigate(`/team/collaboratori/${res.id}`, { replace: true });
      },
      onError: (err: any) => setSaveError(err?.message ?? "Errore durante il salvataggio"),
    };
    if (isNew) create.mutate(clean as any, opts);
    else update.mutate({ id: Number(id), data: clean as any }, opts as any);
  };

  const isPending = create.isPending || update.isPending;

  if (!isNew && isLoading) return <div className="p-6 text-sm text-muted-foreground">Caricamento...</div>;

  return (
    <div className="max-w-2xl mx-auto space-y-4">
      {/* Header */}
      {!isAdmin && (
        <div className="px-4 py-3 rounded-xl bg-amber-50 border border-amber-200 text-amber-700 text-sm">
          Solo gli amministratori possono modificare i collaboratori.
        </div>
      )}

      <div className="flex items-center gap-3">
        <button onClick={goBack} className="w-8 h-8 flex items-center justify-center rounded-xl border border-border hover:bg-muted/40 transition-colors text-muted-foreground flex-shrink-0">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div>
          <h1 className="text-lg font-bold text-foreground leading-tight">
            {isNew ? "Nuovo collaboratore" : `${form.nome} ${form.cognome}`.trim() || "Modifica collaboratore"}
          </h1>
          <p className="text-xs text-muted-foreground mt-0.5">{isNew ? "Aggiungi un collaboratore" : "Modifica le informazioni"}</p>
        </div>
      </div>

      {saveError && (
        <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-red-700 text-sm">
          {saveError}
        </div>
      )}

      <Section icon={User} title="Dati personali">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Nome *">
            <input className={inp} value={form.nome} onChange={e => set("nome", e.target.value)} placeholder="Es. Marco" />
          </Field>
          <Field label="Cognome">
            <input className={inp} value={form.cognome} onChange={e => set("cognome", e.target.value)} placeholder="Es. Rossi" />
          </Field>
        </div>
        <Field label="Email">
          <input className={inp} type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="nome@email.com" />
        </Field>
        <Field label="Telefono">
          <input className={inp} value={form.telefono} onChange={e => set("telefono", e.target.value)} placeholder="+39 333 000 0000" />
        </Field>
      </Section>

      <Section icon={Briefcase} title="Ruoli">
        <div className="flex flex-wrap gap-2">
          {TUTTI_RUOLI.map(r => {
            const sel = ruoli.includes(r);
            return (
              <button key={r} type="button" onClick={() => toggleRuolo(r)}
                className={`px-3 py-2 rounded-xl text-sm font-medium border transition-all ${
                  sel ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary hover:text-primary"
                }`}>
                {r}
              </button>
            );
          })}
        </div>
      </Section>

      <Section icon={Building2} title="Strutture assegnate">
        {(strutture as any[]).length === 0 && <p className="text-sm text-muted-foreground">Nessuna struttura disponibile</p>}
        <div className="space-y-2">
          {(strutture as any[]).map((s: any) => {
            const checked = strutture_ids.includes(s.id);
            return (
              <div key={s.id} onClick={() => toggleStruttura(s)}
                className={`flex items-center gap-3 p-3 rounded-xl border cursor-pointer transition-all ${
                  checked ? "border-primary bg-primary/5" : "border-border hover:border-primary/40"
                }`}>
                <div className={`w-4 h-4 rounded border flex-shrink-0 flex items-center justify-center transition-colors ${
                  checked ? "bg-primary border-primary" : "border-border"
                }`}>
                  {checked && <svg className="w-2.5 h-2.5 text-white" viewBox="0 0 10 10" fill="none"><path d="M1.5 5l2.5 2.5 4.5-4.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" /></svg>}
                </div>
                <div>
                  <div className="text-sm font-medium text-foreground">{s.nome}</div>
                  {s.citta && <div className="text-xs text-muted-foreground">{s.citta}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </Section>

      <Section icon={ToggleRight} title="Stato">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium text-foreground">Collaboratore attivo</span>
          <button type="button" onClick={() => set("attivo", !form.attivo)}
            className={`relative w-11 h-6 rounded-full transition-colors flex-shrink-0 ${form.attivo ? "bg-primary" : "bg-muted-foreground/30"}`}>
            <span className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all ${form.attivo ? "left-6" : "left-1"}`} />
          </button>
        </div>
      </Section>

      <Section icon={FileText} title="Note">
        <textarea className={`${inp} resize-none`} rows={3} value={form.note}
          onChange={e => set("note", e.target.value)} placeholder="Note opzionali..." />
      </Section>

      <div className="flex gap-3 pb-4">
        <button type="button" onClick={goBack} className="flex-1 py-3 rounded-xl border border-border text-sm font-medium text-muted-foreground hover:bg-muted/40 transition-colors">Annulla</button>
        <button
          onClick={handleSave}
          disabled={isPending || !form.nome || !isAdmin}
          className="px-4 py-2.5 rounded-xl bg-primary text-white text-sm font-medium hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
        >  {saved ? <><CheckCircle className="w-4 h-4" /> Salvato!</> : isPending ? "Salvataggio..." : isNew ? "Crea collaboratore" : "Salva modifiche"}
        </button>
      </div>
    </div>
  );
}
